// Drop-in replacement for the Google Places AutocompleteService pattern used
// across the web app's MUI <Autocomplete> inputs. Instead of returning place_id
// predictions that then require a separate Geocoder call, Mapbox search results
// already carry coordinates, so each option includes { lat, lng }.
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import ConfigurableValues from "../config/constants";
import { useLocationContext } from "../context/Location";
import { searchPlaces } from "../utils/mapbox";

// The browser's own position ("lng,lat"), but only when the visitor has
// already allowed location access — this never shows the permission prompt.
// Looked up once per page load and shared by every picker.
let devicePoint = null;
let devicePointRequested = false;
function loadDevicePoint() {
  if (devicePointRequested) return;
  devicePointRequested = true;
  try {
    if (!navigator.permissions || !navigator.geolocation) return;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (status.state !== "granted") return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            devicePoint = `${pos.coords.longitude},${pos.coords.latitude}`;
          },
          () => {},
          { maximumAge: 10 * 60 * 1000, timeout: 10000 }
        );
      })
      .catch(() => {});
  } catch (e) {
    // Older browsers without the Permissions API fall back to the next point.
  }
}

// Returns option objects shaped to be largely compatible with the prior Google
// predictions: `description` (label) + `lat`/`lng` + `placeName`.
export default function useMapboxAutocomplete() {
  const { MAPBOX_TOKEN } = ConfigurableValues();
  const { location } = useLocationContext() || {};
  const [options, setOptions] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    loadDevicePoint();
  }, []);

  // Rank suggestions nearest-first: where the visitor actually is, else the
  // delivery address they picked, else Mapbox's IP guess (searchPlaces' default).
  // Kept in a ref so fetchPredictions stays stable — callers re-run their
  // search effect whenever it changes.
  const savedPoint = useRef(null);
  savedPoint.current =
    location?.latitude && location?.longitude
      ? `${location.longitude},${location.latitude}`
      : null;

  const fetchPredictions = useCallback(
    (input) => {
      if (timer.current) clearTimeout(timer.current);
      if (!input) {
        setOptions([]);
        return;
      }
      // Debounce to avoid hammering the geocoding endpoint on each keystroke.
      timer.current = setTimeout(async () => {
        try {
          const results = await searchPlaces(input, MAPBOX_TOKEN, {
            limit: 5,
            proximity: devicePoint || savedPoint.current,
          });
          setOptions(
            results.map((r) => ({
              description: r.placeName,
              structured_formatting: {
                main_text: r.name,
                secondary_text: r.placeName,
              },
              place_id: r.id,
              lat: r.lat,
              lng: r.lng,
              bbox: r.bbox,
            }))
          );
        } catch (e) {
          setOptions([]);
        }
      }, 300);
    },
    [MAPBOX_TOKEN]
  );

  return useMemo(
    () => ({ options, setOptions, fetchPredictions }),
    [options, fetchPredictions]
  );
}
